<template>
  <div class="chart-wrap">
    <div :class="setChartClass()">
      <PieChart v-if="this.type === 'pie'" :chartData="fillData()" :settings="setPieOptions()" :height="setHeight('pie')" :width="setWidth('pie')" :chartId="getChartId()"></PieChart>      
      <LineChart v-if="this.type === 'line'" :chartData="fillData()" :height="setHeight('line')" :width="setWidth('line')" :settings="setLineOptions()" :chartId="getChartId()"></LineChart>
      <BarChart v-if="this.type === 'bar'" :chartData="fillData()" :height="setHeight('bar')" :width="setWidth('bar')" :settings="setBarOptions()" :chartId="getChartId()"></BarChart>
      <HorizontalBarChart v-if="this.type === 'horizontalBar'" :chartData="fillData()" :height="250" :settings="setBarOptions()" :chartId="getChartId()"></HorizontalBarChart>
      <div :id="getLegendId()" class="chart-legend"></div>
    </div>
    <div :class="setChartClass(true)">
      <PieChart v-if="this.type === 'pie'" :chartData="fillData()" :settings="setPieOptions(true)" :height="setHeight('pie')" :width="setWidth('pie')" :chartId="getChartId(true)"></PieChart>      
      <LineChart v-if="this.type === 'line'" :chartData="fillData()" :height="setHeight('line')" :width="setWidth('line')" :settings="setLineOptions(true)" :chartId="getChartId(true)"></LineChart>
      <BarChart v-if="this.type === 'bar'" :chartData="fillData()" :height="setHeight('bar')" :width="setWidth('bar')" :settings="setBarOptions(true)" :chartId="getChartId(true)"></BarChart>
      <HorizontalBarChart v-if="this.type === 'horizontalBar'" :chartData="fillData()" :height="250" :settings="setBarOptions" :chartId="getChartId(true)"></HorizontalBarChart>
      <div :id="getLegendId(true)" class="chart-legend"></div>
    </div>    
  </div>
</template>

<script>

import LineChart from './charts/ChartLine.vue'
import PieChart from './charts/ChartPie.vue'
import BarChart from './charts/ChartBar.vue'
import HorizontalBarChart from './charts/ChartHorizontalBar.vue'

Chart.Tooltip.positioners.custom = function(elements, position) {
    if (!elements.length) {
      return false;
    }

    return {
      x: position.x,
      y: (position.y < 150) ? 150 : position.y
    }
}

export default {

  components: {

    LineChart,
    PieChart,
    BarChart,
    HorizontalBarChart,

  },

  data: function() {

    return {

      datacollection: null,

      colours: [

        "#f9c013",  // yellow
        "#34abfe",  // blue
        "#6fc238",  // green
        "#105da1",  // teal
        "#f6336a",  // red
        "#fe783d",  // orange

        "#e5f58a",  // sunshine
        "#a5a5f9",  // violet
        "#76e9cf",  // teal
        "#f297e6",  // rose
        "#f4b468",  // honey        
        "#fb9694",  // salmon    

      ],

    }

  },

  mounted: function () {

    this.fillData();

  },

  render: function() {

    if ( this.$data._chart) {
      this.$data._chart.destroy();
    }

  },

  methods: { 


    /**
     * ---------------------------------------------------------------------------- /
     * 
     * SECTION / UI METHODS
     *
     * These methods are used to control the output of different sections within
     * the component, and assign certain dynamic classes to UI elements within the 
     * component.
     * 
     * --------------------------------------------------------------------------- */

    /**
     * METHOD: setChartClass()
     * @param {Boolean} pdf - determines if the chart should be rendered for use in the PDF
     *
     * This method is used to generate the class that is applied to the wrapping div containing the chart.
     * The primary use of this wrapping element is to hide the charts used for PDF generation from the
     * on screen application, while keeping them available for inclusion in the PDF.
     */
    setChartClass(pdf = false) {

      var c = 'chart';

      if ( this.type !== undefined ) {

        c += ' chart--' + this.type;

      }

      if ( pdf ) {

        c += ' chart--pdf';

      }

      return c;

    },


    /**
     * METHOD: setWidth()
     * @param {String} - a string indicating the type of chart
     *
     * This method is used to define the width of the chart as it is rendered within the
     * application. It first checks to see if the generic chart component has an explicitly
     * declared width. If it does, it returns that value. Otherwise, it uses the type parameter to
     * set the width value according to the builtin defaults.
     */
    setWidth(type) {

      if ( this.width ) {

        return Number(this.width);

      }

      switch (type) {

        case 'pie':
          return 400;
          break;

        case 'bar':
          return 840;
          break;

        case 'line':
          return 840;
          break;

      }

    },


    /**
     * METHOD: setHeight()
     * @param {String} - a string indicating the type of chart
     *
     * This method is used to define the height of the chart as it is rendered within the
     * application. It first checks to see if the generic chart component has an explicitly
     * declared height. If it does, it returns that value. Otherwise, it uses the type parameter to
     * set the height value according to the builtin defaults.
     */
    setHeight(type) {

      if ( this.height ) {

        return Number(this.height);
        
      }
      switch (type) {

        case 'pie':
          return 400;
          break;

        case 'bar':
          return 450;
          break;

        case 'line':
          return 450;
          break;
          
      }

    },    


    /**
     * METHOD: getChartId()
     * @param  {Boolean} pdf - determines if the chart should be rendered for use in the PDF
     * @return {String} - the returned chart ID
     *
     * This method generates an ID for the chart, based on the value passed into the generic chart
     * component's id property. This function is intended to pass the data into the chartId property
     * of the individual chart components. 
     *
     * It also checks to determine if the chart is meant for use in a PDF. If it is, it appends
     * a PDF identifier to the end of the ID.
     */
    getChartId(pdf = false) {

      var id = this.id;

      if ( id === '' ) {

        id = 'chart';

      }

      if ( pdf ) {

        id += 'PDF';

      }

      return id;

    },


    /**
     * METHOD: getLegendId()
     * @return {String} - This method generates an ID for the chart legend, based on the value passed into the 
     * generic chart component's id property.
     */
    getLegendId() {

      var id = this.id;

      if ( id === '' ) {

        id = 'chart';

      }

      return id + '__legend';      

    },


    /**
     * ---------------------------------------------------------------------------- /
     * 
     * CHART / DATA METHODS
     *
     * These methods are used to define the options and data sets that are used to
     * actually generate the charts used in the applications various tools and reports.
     * 
     * --------------------------------------------------------------------------- */

    /**
     * METHOD: setBarOptions()
     * @param {Boolean} pdf - determines if the chart should be rendered for use in the PDF
     *
     * This method generates the object that will be passed to the chart.js chart via the settings
     * property. It checks a number of this components properties in order compute the settings, providing
     * property-driven control of the charts.
     */
    setBarOptions(pdf = false) {

      var self = this;
      var options = {
      
        responsive: false, 
        maintainAspectRatio: true,
        forPDF: pdf,

        layout: {

          padding: {

            top: 30,

          },

        },

        scales: {
          xAxes: [{
            barPercentage: 0.65,
            categoryPercentage: 0.65,
            stacked: (this.stacked === 'true') ? true : false, 
            ticks: {
              beginAtZero: true,
              fontColor: pdf ? '#000' : '#ddd',
              // Include a dollar sign in the ticks
              callback: (this.type === 'horizontalBar') ? 
                function(value, index, values) {
                  return '$' + Math.ceil(value / 1000) + 'K';
              } : 
                function(value, index, values) {
                  return value;
              },
            },
            gridLines: {
              display: false ,
              color: pdf ? '#ddd' : "#555",
            },                
          }],
          yAxes: [{
            stacked: (this.stacked === 'true') ? true : false,
            ticks: {
              beginAtZero: true,
              fontColor: pdf ? '#000' : '#ddd',
              // Include a dollar sign in the ticks
              callback: (this.type === 'bar') ? 
                function(value, index, values) {
                  return window.formatCurrencyShorthand(value, false);
              } : 
                function(value, index, values) {
                  return value;
              },  
            },
            gridLines: {
              display: true,
              color: pdf ? '#ddd' : "#555",
            },             
          }],        
        },

        legend: (this.legend === 'false') ? false : {

          display: false, 

        },

        legendCallback: function(chart) {
          
          if ( chart.legend !== undefined ) {

            window.createLegend(chart.canvas.id, chart.legend.legendItems);

          }

        },        

        animation: {},

        tooltips: {

          position: 'nearest',
          callbacks: {

            label: function(tooltipItem, data) {

              var split = data.datasets[tooltipItem.datasetIndex].label.split('::');

              return ' ' + self._translate(split[0]) + ': ' + 
                window.formatCurrency(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index], false);

            }

          }

        },           

        afterFit: function() {
          this.height = this.height + 50;
        },

      };

      if ( this.yFormat === 'percentage' ) {

        options.scales.yAxes[0].ticks.callback = function(value, index, values) {

            return window.formatPercentage(value / 100, false);

        };

      }

      if ( this.tFormat === 'percentage' ) {

        options.tooltips.callbacks.label = function(tooltipItem, data) {

          return ' rate required: ' + window.formatPercentage(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] / 100);

        };

      }

      if ( this.labelPeak === 'true' ) {

        options.events = false;
        options.showTooltips = false;

        options.tooltips.position = 'custom';

        options.animation.duration = 500,
        options.animation.onComplete = function() {

          var chart = this.chart,
              ctx = chart.ctx,
              height = chart.controller.boxes[0].bottom;

            ctx.textBaseline = 'top';
            ctx.textAlign = 'center';
            ctx.fillStyle = '#aaa';           

            Chart.helpers.each(this.data.datasets.forEach(function (dataset, i) {

              var meta = chart.controller.getDatasetMeta(i);

              Chart.helpers.each(meta.data.forEach(function (bar, index) {

                ctx.fillText(window.formatPercentage(dataset.data[index] / 100), bar._model.x, bar._model.y - 20);

              }),this)

            }),this);

            chart.options.animation.enabled = false;

        };

      }    

      return options;

    },


    /**
     * METHOD: setLineOptions()
     * @param {Boolean} pdf - determines if the chart should be rendered for use in the PDF
     *
     * This method generates the object that will be passed to the chart.js chart via the settings
     * property. It checks a number of this components properties in order compute the settings, providing
     * property-driven control of the charts.
     */
    setLineOptions(pdf = false) {

      var self = this;
      var options = {
      
        responsive: false, 
        maintainAspectRatio: true,
        forPDF: pdf,
        animation: false,

        scales: {
          xAxes: [{
            barPercentage: 0.65,
            categoryPercentage: 0.65,
            stacked: (this.stacked === 'true') ? true : false,
            ticks: {
              fontColor: pdf ? '#000' : '#ddd',
              maxRotation: 0,
              autoSkip: false,
            },
            gridLines: {
              display: false ,
              color: pdf ? '#ddd' : "rgba(255, 255, 255, 0.2)",
            },
          }],
          yAxes: [{
            stacked: (this.stacked === 'true') ? true : false,
            ticks: {
              fontColor: pdf ? '#000' : '#ddd',
              beginAtZero: true,
              // Include a dollar sign in the ticks
              callback: function(value, index, values) {
                  return window.formatCurrencyShorthand(value, false);
              }              
            },
            gridLines: {
              display: true,
              color: pdf ? '#ddd' : "rgba(255, 255, 255, 0.05)",
            },            
          }],        
        },       

        legend: {

          display: false,

        },

        legendCallback: function(chart) {
          
          if ( chart.legend !== undefined ) {

            window.createLegend(chart.canvas.id, chart.legend.legendItems, 'strokeStyle');

          }

        },        

        tooltips: {

          callbacks: {

            label: function(tooltipItem, data) {

              return ' ' + self._translate(data.datasets[tooltipItem.datasetIndex].label) + ': ' + 
                           window.formatCurrency(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index], false);

            }

          }

        },        

        afterFit: function() {
          this.height = this.height + 50;
        },      

      };

      return options;

    },


    /**
     * METHOD: setPieOptions()
     * @param {Boolean} pdf - determines if the chart should be rendered for use in the PDF
     *
     * This method generates the object that will be passed to the chart.js chart via the settings
     * property. It checks a number of this components properties in order compute the settings, providing
     * property-driven control of the charts.
     */
    setPieOptions(pdf = false) {

      var self = this;
      return {
      
        responsive: false, 
        maintainAspectRatio: true,
        forPDF: pdf,
        legend: {

          display: false,

        },

        legendCallback: function(chart) {
          
          if ( chart.legend !== undefined ) {

            window.createLegend(chart.canvas.id, chart.legend.legendItems);

          }

        },        

        tooltips: {

          callbacks: {

            label: function(tooltipItem, data) {

              var split = data.labels[tooltipItem.index].split('::');

              return ' ' + self._translate(split[0]) + ': ' + window.formatCurrency(data.datasets[0].data[tooltipItem.index], false);

            }

          }

        },

      }

    },       


    /**
     * METHOD fillData()
     * @return {Object} - return the data that should be used for building out the chart.
     *
     * This generic method checks the chart component's type method and uses that value to execute the
     * appropriate method to convert the chart data from the report or tool component into a format
     * that can be used by the charts.
     *
     * It may seem counter-productive to have the report components generate chart data and then
     * allow the chart component to re-build that data, but there are certain key conversions that 
     * need to be done specifically for different chart types, and it makes more sense to allow those
     * process to run in this singular location than to replicate them across the different reports.
     */
    fillData () {

      if ( this.type === 'pie' ) {

        var chartData = this.getPieChartData();

      }

      else if ( this.type === 'bar' || this.type === 'horizontalBar') {

        var chartData = this.getBarChartData();

      }

      else if ( this.type === 'line' ) {

        var chartData = this.getLineChartData();

      }      

      return chartData;

    },


    /**
     * METHOD getPieChartData()
     * @return {Object} - the final chart data to be used by chart.js to build the the pie chart
     *
     * This method is used to build out the data for the pie chart. It loops over the elements
     * in the chart's data property, setting the values and applying a sequential colour.
     */
    getPieChartData() {

      var chartData = {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
            borderWidth: 0,
          }
        ],
      };

      var i = 0;
      for ( var k in this.data ) {

        chartData.labels.push(this.data[k].label);
        chartData.datasets[0].data.push(this.data[k].value);

        var backgroundColor = (this.data[k].color === undefined ) ? this.cycleColours(i + 1) : this.$data.colours[this.data[k].color];
        chartData.datasets[0].backgroundColor.push(backgroundColor);

        i += 1;

      }

      return chartData;

    },


    /**
     * METHOD getBarChartData()
     * @return {Object} - the final chart data to be used by chart.js to build the the bar chart
     *
     * This method is used to build out the data for the bar chart. It loops over the elements
     * in the chart's data property, other than the labels and otherData properties, and sets the
     * data for each bar, along with the specified colour. If there is no colour specified, a colour
     * is sequentially applied from the colours array. 
     */
    getBarChartData() {

      var chartData = {
        labels: this.data.labels,
        datasets: [
        ],
      };

      var x = 0;
      for ( var i in this.data ) {

        if ( i !== 'labels' && i !== 'otherData' ) {

          var backgroundColor = (this.data[i].color === undefined ) ? this.cycleColours(x + 1) : this.$data.colours[this.data[i].color];

          chartData.datasets[x] = {
            data: this.data[i].data,
            label: (this.data[i].label === undefined) ? '' : this.data[i].label,
            backgroundColor: backgroundColor,
          };

          x += 1;

        }

      }

      return chartData;

    },


    /**
     * METHOD getLineChartData()
     * @return {Object} - the final chart data to be used by chart.js to build the the line chart
     *
     * This method is used to build out the data for the line chart. It loops over the elements
     * in the chart's data property, setting the values for the line chart, but skipping the 
     * labels property. The function also sets a number of the key properties that are required 
     * for the more custom visual display that we've applied to the line charts in the application.
     */
    getLineChartData() {

      var chartData = {
        labels: this.data.labels,
        datasets: [
        ]
      };

      var x = 0;
      for ( var i in this.data ) {

        if ( i !== 'labels' ) {

          var backgroundColor = (this.data[i].color === undefined ) ? this.cycleColours(x + 1) : this.$data.colours[this.data[i].color];
          var borderWidth = (this.data[i].borderWidth === undefined ) ? 2 : this.data[i].borderWidth;
          var pointRadius = (this.data[i].pointRadius === undefined ) ? 2 : this.data[i].pointRadius;

          var ctx = document.getElementById('ctx').getContext("2d");
          var graidient = ctx.createLinearGradient(0, 400, 0, 0);
          graidient.addColorStop(0, window.hexToRGBA(backgroundColor, '.05'));
          graidient.addColorStop(1, window.hexToRGBA(backgroundColor, '.65'));

          chartData.datasets[x] = {
            data: this.data[i].data,
            label: (this.data[i].label === undefined) ? '' : this.data[i].label,
            borderColor: backgroundColor,
            pointBackgroundColor: '#fff',
            fill: 'start',
            backgroundColor: graidient, //window.hexToRGBA(backgroundColor, '.05'),
            lineTension: .4,
            borderWidth: borderWidth,
            pointRadius: pointRadius,
            spanGaps: true,
          };

          x += 1;

        }

      }

      return chartData;

    },



    /**
     * ---------------------------------------------------------------------------- /
     * 
     * COLOUR METHODS
     *
     * These methods are used to control to provide some control over the colours
     * used in the charts.
     * 
     * --------------------------------------------------------------------------- */

    /**
     * METHOD: cycleColours()
     * @param  {Number} x - index of the colour to retrieve.
     * @return {String} - returns the colour variable from the colours array, based on the index number.
     *
     * This method is used to retrieve a specific colour from list of colours in the components
     * colour data property (array). It also checks to determine if the index number is larger than 
     * the total number of items in the array, and if it is, sets the index back to 0.
     *
     * The idea with this function is to be able to work through a colour cycle, applying colours to
     * chart data in a sequential and predictable pattern.
     */
    cycleColours(x) {

      var max = this.$data.colours.length;

      while ( x > max ) {

        x -= max;

      }

      return this.$data.colours[x - 1];

    },


    /**
     * METHOD: shadeColor1()
     * @param  {HEX} color - the hex code of the starting colour.
     * @param  {Number} percent - the percentage change of the colour
     * @return {HEX} - the new colour after the process
     *
     * This method will adjust the hex code of a particular colour by the given percentage defined
     * in the percent parameter. A positive percentage will lighten the colour, while a negative percentage
     * will darken the colour.
     *
     * The method is not currently used, but remains here for potential future use.
     * Depending on future use, this function may be added as a mixin to the global Vue instance.
     */
      shadeColor1(color, percent) {  

        if ( color.substr(0,1) === '#' ) {

          color = color.substr(1);

        }

          var num = parseInt(color, 16),
          amt = Math.round(2.55 * percent),
          R = (num >> 16) + amt,
          G = (num >> 8 & 0x00FF) + amt,
          B = (num & 0x0000FF) + amt;
          return '#' + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
      },    

  },

  props: [

    'data',
    'type',
    'stacked',
    'legend',
    'id',
    'width',
    'height',

    'yFormat',
    'tFormat',
    'labelPeak',

  ]

}

</script>

<style lang="scss">


  .chart-wrap { 

    position: relative;

  }

  .chart {

    position: relative;
    z-index: 2;
    padding: 1.5rem;
    /* Permalink - use to edit and share this gradient: http://colorzilla.com/gradient-editor/#444448+0,333338+100 */
    background: #484a4e; /* Old browsers */
    background: -moz-linear-gradient(top, #333338 0%, #222228 100%); /* FF3.6-15 */
    background: -webkit-linear-gradient(top, #333338 0%,#222228 100%); /* Chrome10-25,Safari5.1-6 */
    background: linear-gradient(to bottom, #333338 0%,#222228 100%); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */

    canvas {

      height: auto !important;
      max-width: 100%;

    }

  }

  .chart--pdf {

    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    z-index: 1;
    opacity: 0;

  }

  .chart--pie {

    padding-top: 3rem;

    canvas {

      margin-left: auto;
      margin-right: auto;

    }

    .chartLegend {

      max-width: 400px;
      margin-left: auto;
      margin-right: auto;

    }

  }

  .chartLegend {

    list-style-type: none;
    padding: 0;
    text-align: center;
    font-size: .75rem;
    margin-top: 1.5rem;

  }

  .chartLegend__item {

    display: inline-block;
    margin-right: .8rem;
    margin-bottom: .5rem;
    color: #ddd;

  }

  .chartLegend__color {

    display: inline-block;
    width: 1em;
    height: 1em;
    background: #ddd;
    margin-right: .5em;

    transform: translate3d(0, 2px, 0);

  }

  .chart-slider {

    overflow: hidden;

  }

  .chart-slider__wrapper {

    width: 200%;
    display: flex;

    transform: translate3d(0, 0, 0);

  }

  .chart-slider__chart {

    width: 50%;

  }

</style>



// WEBPACK FOOTER //
// Chart.vue?01286e67